import BaseApiService from './BaseApiService'
import type { RenderJob, BatchRenderResult } from '../types'
import type { CanonicalDocument } from '../cdm/types'

export type OutputFormat = 'pdf' | 'docx' | 'xlsx' | 'pptx'

class RenderApiService extends BaseApiService {
  generate(
    templateVersionId: string,
    payloadJson: string,
    options?: { onMissing?: 'blank' | 'error' | 'hide'; outputFormat?: OutputFormat; emailTo?: string; watermark?: boolean; watermarkText?: string; deliverToUrl?: string; locale?: string }
  ): Promise<Blob> {
    return this.postBlob('/v1/render/generate', {
      templateVersionId,
      payloadJson,
      locale: options?.locale || undefined,
      onMissing: options?.onMissing,
      outputFormat: options?.outputFormat,
      emailTo: options?.emailTo || undefined,
      deliverToUrl: options?.deliverToUrl || undefined,
      watermark: options?.watermark ?? false,
      watermarkText: options?.watermarkText || undefined,
    })
  }

  mergeGenerate(
    templateVersionId: string,
    payloads: string[],
    options?: { onMissing?: 'blank' | 'error' | 'hide'; outputFormat?: OutputFormat; watermark?: boolean; watermarkText?: string }
  ): Promise<Blob> {
    return this.postBlob('/v1/render/merge-generate', {
      templateVersionId,
      payloads,
      onMissing: options?.onMissing,
      outputFormat: options?.outputFormat,
      watermark: options?.watermark ?? false,
      watermarkText: options?.watermarkText || undefined,
    })
  }

  generateToCloud(
    templateVersionId: string,
    payloadJson: string,
    options: { onMissing?: 'blank' | 'error' | 'hide'; outputFormat?: OutputFormat; watermark?: boolean; cloudStorageConnectionId: string; cloudStoragePath?: string; cloudStorageFileName?: string }
  ): Promise<{ cloudFileUrl?: string }> {
    return this.post('/v1/render/generate', {
      templateVersionId,
      payloadJson,
      onMissing: options.onMissing,
      outputFormat: options.outputFormat,
      watermark: options.watermark ?? false,
      cloudStorageConnectionId: options.cloudStorageConnectionId,
      cloudStoragePath: options.cloudStoragePath || undefined,
      cloudStorageFileName: options.cloudStorageFileName || undefined,
    })
  }

  preview(
    templateVersionId: string,
    payloadJson: string,
    format: 'json' | 'xml' = 'json',
    options?: { onMissing?: 'blank' | 'error' | 'hide'; outputFormat?: OutputFormat; signal?: AbortSignal; locale?: string }
  ): Promise<Blob> {
    return this.postBlob('/v1/render/preview', {
      templateVersionId,
      payloadJson,
      format,
      locale: options?.locale || undefined,
      onMissing: options?.onMissing,
      outputFormat: options?.outputFormat,
    }, options?.signal)
  }

  /**
   * Renders an unsaved WYSIWYG-editor CanonicalDocument directly — nothing is persisted (no
   * render_job row, no usage event). `templateVersionId` is optional and used only to resolve
   * image assets the document references by blobRef but doesn't carry inline; when given, the
   * caller must own that version.
   */
  previewCanonical(
    canonicalDocument: CanonicalDocument,
    payloadJson: string,
    options?: { onMissing?: 'blank' | 'error' | 'hide'; outputFormat?: OutputFormat; watermark?: boolean; watermarkText?: string; templateVersionId?: string; templateId?: string; signal?: AbortSignal; locale?: string }
  ): Promise<Blob> {
    return this.postBlob('/v1/render/preview-canonical', {
      canonicalJson: JSON.stringify(canonicalDocument),
      payloadJson,
      locale: options?.locale || undefined,
      onMissing: options?.onMissing,
      outputFormat: options?.outputFormat,
      watermark: options?.watermark ?? false,
      watermarkText: options?.watermarkText || undefined,
      templateVersionId: options?.templateVersionId,
      templateId: options?.templateId,
    }, options?.signal)
  }

  renderInline(
    templateBase64: string,
    payloadJson: string,
    options?: { onMissing?: 'blank' | 'error' | 'hide'; outputFormat?: OutputFormat }
  ): Promise<Blob> {
    return this.postBlob('/v1/render/inline', {
      template: templateBase64,
      payloadJson,
      onMissing: options?.onMissing,
      outputFormat: options?.outputFormat,
    })
  }

  downloadOutput(renderJobId: string): Promise<Blob> {
    return this.postBlob('/v1/render/download', { renderJobId })
  }

  getJob(renderJobId: string): Promise<RenderJob> {
    return this.post<RenderJob>('/v1/render/job', { renderJobId })
  }

  listJobs(opts?: { templateVersionId?: string; status?: number; page?: number; pageSize?: number; from?: string; to?: string }): Promise<RenderJob[]> {
    return this.post<RenderJob[]>('/v1/render/jobs', {
      templateVersionId: opts?.templateVersionId,
      status: opts?.status,
      page: opts?.page ?? 1,
      pageSize: opts?.pageSize ?? 50,
      from: opts?.from,
      to: opts?.to,
    })
  }

  exportJobsUrl(opts?: { status?: number; from?: string; to?: string }): string {
    const params = new URLSearchParams()
    if (opts?.status != null) params.set('status', String(opts.status))
    if (opts?.from) params.set('from', opts.from)
    if (opts?.to) params.set('to', opts.to)
    return `/api/v1/render/jobs/export?${params}`
  }

  batch(templateVersionId: string, payloads: string[], options?: { onMissing?: string; outputFormat?: string }): Promise<BatchRenderResult[]> {
    return this.post('/v1/render/batch', { templateVersionId, payloads, ...options })
  }

  batchZip(templateVersionId: string, payloads: string[], options?: { onMissing?: string; outputFormat?: string }): Promise<Blob> {
    return this.postBlob('/v1/render/batch/zip', { templateVersionId, payloads, ...options })
  }

  createShare(renderJobId: string, opts?: { ttlHours?: number; password?: string; maxDownloads?: number }): Promise<{ shareToken: string; expiresAt: string; shareUrl: string; passwordProtected: boolean }> {
    return this.post('/v1/render/share', { renderJobId, ttlHours: opts?.ttlHours ?? 24, password: opts?.password, maxDownloads: opts?.maxDownloads })
  }

  batchCsv(
    templateVersionId: string,
    file: File,
    options?: { onMissing?: string; outputFormat?: string }
  ): Promise<Array<{ rowIndex: number; renderJobId: string; success: boolean; errorMessage: string | null }>> {
    const form = new FormData()
    form.append('templateVersionId', templateVersionId)
    form.append('file', file)
    if (options?.onMissing) form.append('onMissing', options.onMissing)
    if (options?.outputFormat) form.append('outputFormat', options.outputFormat)
    return this.postForm('/v1/render/batch/csv', form)
  }

  createPreviewShare(
    templateVersionId: string,
    payloadJson: string,
    opts?: { outputFormat?: string; ttlHours?: number; password?: string; maxViews?: number }
  ): Promise<{ shareToken: string; expiresAt: string; shareUrl: string; passwordProtected: boolean }> {
    return this.post('/v1/render/preview-share', {
      templateVersionId,
      payloadJson,
      outputFormat: opts?.outputFormat,
      ttlHours: opts?.ttlHours ?? 24,
      password: opts?.password,
      maxViews: opts?.maxViews,
    })
  }

  rerender(renderJobId: string, options?: { onMissing?: string; outputFormat?: string }): Promise<{ renderJobId: string; pageCount: number | null }> {
    return this.post('/v1/render/rerender', { renderJobId, ...options })
  }

  submit(
    templateVersionId: string,
    payloadJson: string,
    options?: { onMissing?: string; outputFormat?: string; watermark?: boolean }
  ): Promise<{ renderJobId: string }> {
    return this.post('/v1/render/submit', {
      templateVersionId,
      payloadJson,
      format: 'json',
      ...options,
    })
  }

  pollJob(renderJobId: string): Promise<{ renderJobId: string; statusId: number; outputBlobPath: string | null; errorMessage: string | null; pageCount: number | null }> {
    return this.post('/v1/render/job', { renderJobId })
  }

  batchXlsx(
    templateVersionId: string,
    file: File,
    options?: { onMissing?: string; outputFormat?: string }
  ): Promise<Array<{ rowIndex: number; renderJobId: string; success: boolean; errorMessage: string | null }>> {
    const form = new FormData()
    form.append('templateVersionId', templateVersionId)
    form.append('file', file)
    if (options?.onMissing) form.append('onMissing', options.onMissing)
    if (options?.outputFormat) form.append('outputFormat', options.outputFormat)
    return this.postForm('/v1/render/batch/xlsx', form)
  }

  getTrace(jobId: string): Promise<RenderTrace> {
    return this.get<RenderTrace>(`/v1/render/jobs/${jobId}/trace`)
  }

  verifyOutput(jobId: string): Promise<{ intact: boolean; storedHash: string; actualHash: string }> {
    return this.post(`/v1/render/jobs/${jobId}/verify`, {})
  }

  validate(templateVersionId: string, payloadJson: string): Promise<{ valid: boolean; issues: Array<{ key: string; kind: string; issue: string }> }> {
    return this.post('/v1/render/validate', { templateVersionId, payloadJson })
  }

  detectPii(payloadJson: string): Promise<{ detections: Array<{ jsonPath: string; kind: string; sample: string; masked: string }> }> {
    return this.post('/v1/render/detect-pii', { payloadJson })
  }

  compareVersions(
    versionId1: string,
    versionId2: string,
    options?: { payloadJson?: string; outputFormat?: OutputFormat }
  ): Promise<CompareVersionsResponse> {
    return this.post('/v1/render/compare-versions', {
      versionId1,
      versionId2,
      payloadJson: options?.payloadJson,
      outputFormat: options?.outputFormat,
    })
  }
}

export interface CompareVersionResult {
  versionId: string
  versionNumber: number
  success: boolean
  content: string | null
  contentType: string | null
  error: string | null
}

export interface CompareVersionsResponse {
  version1: CompareVersionResult
  version2: CompareVersionResult
}

export interface RenderTrace {
  renderJobId: string
  templateVersionId: string
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'unknown'
  outputFormat: string
  durationMs: number | null
  pageCount: number | null
  outputByteSize: number | null
  outputHash: string | null
  warnings: string[]
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
  hasOutput: boolean
  payloadAvailable: boolean
}

export const renderApiService = new RenderApiService()
